import { useEffect, useState } from "react";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle2, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

export default function PurchaseList() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try { const res = await api.get("/purchase-list"); setItems(res.data || []); }
    catch (error) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  const handleMarkOrdered = async (purchaseId) => {
    setUpdatingId(purchaseId);
    try {
      await api.put(`/purchase-list/${purchaseId}/status`, { status: "ordered" });
      setItems((prev) => prev.filter((it) => it.purchase_id !== purchaseId));
      toast.success(t("purchase.orderPlaced"));
    } catch (error) { toast.error(error.response?.data?.detail || "Error"); }
    finally { setUpdatingId(null); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-list-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground">{t("purchase.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("purchase.subtitle")}</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="font-heading text-lg">{t("purchase.pendingTitle")}</CardTitle>
              <CardDescription>{t("purchase.pendingDesc")}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-muted-foreground">{t("purchase.loading")}</div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground">{t("purchase.empty")}</div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.purchase_id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-zinc-50">
                  <div>
                    <p className="font-medium text-foreground">{item.name}</p>
                    {item.created_at && <p className="text-xs text-muted-foreground">{t("purchase.added")}: {item.created_at.slice(0, 16)}</p>}
                  </div>
                  <Button size="sm" onClick={() => handleMarkOrdered(item.purchase_id)} disabled={updatingId === item.purchase_id} data-testid={`mark-ordered-${item.purchase_id}`}>
                    {updatingId === item.purchase_id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    {t("purchase.orderPlaced")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
